import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Table,
  TableHead, TableRow, TableCell, TableBody,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {
  fetchDataItemsByDataType,
  updateDataItem,
  deleteDataItem,
  fetchDataIndicators,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

const EMPTY_KPI_FORM = { indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" };

export default function DataItemDetailPage() {
  const { businessDomainId, dataTypeId, dataItemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [item, setItem] = useState(location.state || null);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // KPI state
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiEditTarget, setKpiEditTarget] = useState(null);
  const [kpiSelectedInd, setKpiSelectedInd] = useState("");
  const [kpiForm, setKpiForm] = useState(EMPTY_KPI_FORM);
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteTarget, setKpiDeleteTarget] = useState(null);

  const backPath = `/design/${businessDomainId}/data-types/${dataTypeId}`;
  const editPath = `/design/${businessDomainId}/data-types/${dataTypeId}/data-items/${dataItemId}/edit`;

  const loadItem = useCallback(async () => {
    try {
      const items = await fetchDataItemsByDataType(dataTypeId);
      const found = (Array.isArray(items) ? items : []).find((i) => i.dataItemId === dataItemId);
      if (found) setItem(found);
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypeId, dataItemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  useEffect(() => {
    (async () => {
      try { setAvailableIndicators(await fetchDataIndicators()); } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleDelete = async () => {
    try {
      await deleteDataItem(dataItemId);
      notify.success(t("message.delete.success"));
      navigate(backPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  // ── KPI handlers ──
  const openKpiAdd = () => {
    setKpiEditMode(false); setKpiEditTarget(null);
    setKpiSelectedInd(""); setKpiForm(EMPTY_KPI_FORM);
    setKpiDialogOpen(true);
  };
  const openKpiEdit = (kpi) => {
    setKpiEditMode(true); setKpiEditTarget(kpi);
    setKpiSelectedInd("");
    setKpiForm({ indicatorName: kpi.indicatorName || "", unit: kpi.unit || "", measuringMethod: kpi.measuringMethod || "", evaluationStandard: kpi.evaluationStandard || "", description: kpi.description || "" });
    setKpiDialogOpen(true);
  };
  const handleKpiIndSelect = (e) => {
    const selected = e.target.value;
    setKpiSelectedInd(selected);
    if (selected === "OTHER") {
      setKpiForm(EMPTY_KPI_FORM); // 「その他」は全フィールドを自由入力
    } else if (selected && selected !== "") {
      setKpiForm({ indicatorName: selected.indicatorName || "", unit: selected.unit || "", measuringMethod: selected.measuringMethod || "", evaluationStandard: selected.evaluationStandard || "", description: selected.description || "" });
    }
  };
  const handleKpiSave = async () => {
    const existing = item?.indicators || [];
    try {
      if (kpiEditMode) {
        const updated = existing.map((i) =>
          i.indicatorName === kpiEditTarget.indicatorName ? { ...kpiForm, updatedAt: new Date().toISOString() } : i
        );
        await updateDataItem({ ...item, indicators: updated });
        notify.success(t("message.update.success"));
      } else {
        if (!kpiSelectedInd) return;
        if (kpiSelectedInd === "OTHER" && !kpiForm.indicatorName.trim()) {
          notify.error(t("dataKpi.error.nameRequired")); return;
        }
        if (existing.some((i) => i.indicatorName === kpiForm.indicatorName)) {
          notify.error(t("dataKpi.error.duplicate")); return;
        }
        await updateDataItem({ ...item, indicators: [...existing, { ...kpiForm, createdAt: new Date().toISOString() }] });
        notify.success(t("message.create.success"));
      }
      setKpiDialogOpen(false);
      await loadItem();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    const updated = (item?.indicators || []).filter((i) => i.indicatorName !== kpiDeleteTarget.indicatorName);
    try {
      await updateDataItem({ ...item, indicators: updated });
      notify.success(t("message.delete.success"));
      await loadItem();
    } catch (e) { notify.apiError(e); }
    setKpiConfirmOpen(false); setKpiDeleteTarget(null);
  };

  if (!item) return null;

  const kpis = item.indicators || [];

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("dataItem.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(editPath, { state: item })}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      {/* ── Detail fields ── */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("dataItem.name")} value={item.dataItemName} /></Grid>
          <Grid size={12}><DetailField label={t("dataItem.description")} value={item.dataItemDescription} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("dataItem.dataDomain")} value={item.dataDomainName} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("common.url")} value={item.url} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">{t("dataItem.primaryKey")}</Typography>
            <Typography variant="body2">{item.primaryKey ? "✔" : "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">{t("dataItem.foreignKey")}</Typography>
            <Typography variant="body2">{item.foreignKey ? "✔" : "—"}</Typography>
          </Grid>
          {item.foreignKey && (
            <Grid size={12}><DetailField label={t("dataItem.referenceAttributeId")} value={item.referenceAttributeId} /></Grid>
          )}
        </Grid>
      </DetailSection>

      {/* ── KPI section ── */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("dataItem.kpi.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAdd}>{t("common.add")}</Button>
        </Box>
        {kpis.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>—</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("dataKpi.indicatorName")}</TableCell>
                <TableCell>{t("dataKpi.unit")}</TableCell>
                <TableCell>{t("dataKpi.measuringMethod")}</TableCell>
                <TableCell>{t("dataKpi.evaluationStandard")}</TableCell>
                <TableCell width={90}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kpis.map((kpi) => (
                <TableRow key={kpi.indicatorName}>
                  <TableCell>{kpi.indicatorName}</TableCell>
                  <TableCell>{kpi.unit}</TableCell>
                  <TableCell>{kpi.measuringMethod}</TableCell>
                  <TableCell>{kpi.evaluationStandard}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openKpiEdit(kpi)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => { setKpiDeleteTarget(kpi); setKpiConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(backPath)}>{t("common.back")}</Button>
      </Box>

      {/* ── Confirm dialogs ── */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteTarget(null); }} />

      {/* ── KPI Add/Edit Dialog ── */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("dataItem.kpi.edit.title") : t("dataItem.kpi.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {!kpiEditMode ? (
              <FormControl fullWidth required>
                <InputLabel>{t("dataKpi.indicatorName")}</InputLabel>
                <Select value={kpiSelectedInd} label={t("dataKpi.indicatorName")} onChange={handleKpiIndSelect}>
                  {availableIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind}>{ind.indicatorName}</MenuItem>
                  ))}
                  <MenuItem value="OTHER">{t("dataKpi.other")}</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField fullWidth label={t("dataKpi.indicatorName")} value={kpiForm.indicatorName} disabled />
            )}
            {!kpiEditMode && kpiSelectedInd === "OTHER" && (
              <TextField fullWidth required label={t("dataKpi.indicatorName")} value={kpiForm.indicatorName}
                onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            )}
            {(kpiEditMode || kpiSelectedInd !== "") && (
              <>
                <TextField fullWidth label={t("dataKpi.unit")} value={kpiForm.unit}
                  disabled={!kpiEditMode && !!kpiSelectedInd && kpiSelectedInd !== "OTHER"}
                  onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.measuringMethod")} value={kpiForm.measuringMethod}
                  onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.evaluationStandard")} value={kpiForm.evaluationStandard}
                  onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.description")} value={kpiForm.description}
                  onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}
            disabled={!kpiEditMode && (kpiSelectedInd === "" || (kpiSelectedInd === "OTHER" && !kpiForm.indicatorName.trim()))}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
